import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Leaf, Sparkles, Droplets, Package } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';

const IMAGES = {
  oil: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/6jsozhxb_HEALING%20OIL%20ON%20ANNA.jpg',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const Store = () => {
  const { t } = useLanguage();

  const categories = [
    { key: 'supplements', icon: Leaf, color: '#A91B60' },
    { key: 'cosmetics', icon: Sparkles, color: '#C9B99A' },
    { key: 'aromatherapy', icon: Droplets, color: '#A91B60' },
    { key: 'other', icon: Package, color: '#C9B99A' },
  ];

  return (
    <div data-testid="store-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.oil}
            alt="ABBA Store"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {t('store.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {t('store.title')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/80 mb-8 font-['Manrope']">
              {t('store.description')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 md:py-32 px-6" data-testid="store-categories">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {categories.map((category) => (
              <motion.div
                key={category.key}
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer"
              >
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <category.icon className="w-10 h-10" style={{ color: category.color }} />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-2">
                  {t(`store.categories.${category.key}`)}
                </h3>
                <p className="text-sm text-[#5A5A5A] font-['Manrope']">
                  {t('store.comingSoon')}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-8">
              <div className="w-20 h-20 rounded-full bg-[#A91B60] mx-auto flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
              Store <em className="italic text-[#A91B60]">coming soon</em>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] mb-10 font-['Manrope']">
              We're curating the best wellness products for you. Contact us for product inquiries.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.contact')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Store;
